import { callFunctionRaw } from "@/lib/callFunction.js";

function normalizeToken(value = "") {
  return String(value || "").trim();
}

function friendlyCommunicationError(error, fallback) {
  const message = String(error?.message || error || "").trim();
  const lower = message.toLowerCase();

  if (lower.includes("failed to fetch") || lower.includes("network") || lower.includes("timeout")) {
    return new Error(
      "AfriMed™ could not reach communication preferences right now. Please try again later.",
    );
  }

  if (lower.includes("invalid") || lower.includes("expired") || lower.includes("token")) {
    return new Error("This communication link is invalid or has expired.");
  }

  return new Error(message || fallback);
}

function withClientTimeout(promise, timeoutMs = 5000, label = "Communication preferences") {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      const timeoutError = new Error(`${label} timed out`);
      timeoutError.code = "timeout";
      setTimeout(() => reject(timeoutError), timeoutMs);
    }),
  ]);
}

export async function resolveCommunicationPreferences(token) {
  try {
    return await withClientTimeout(
      callFunctionRaw(
        "communication-preferences",
        {
          action: "resolve",
          token: normalizeToken(token),
        },
        {
          skipAuth: true,
          preferFreshSession: false,
          timeoutMs: 4500,
        },
      ),
      5000,
      "Loading communication preferences",
    );
  } catch (error) {
    throw friendlyCommunicationError(
      error,
      "AfriMed™ could not load these communication preferences.",
    );
  }
}

export async function updateCommunicationPreferences({ token = "", preferences = {} } = {}) {
  try {
    return await withClientTimeout(
      callFunctionRaw(
        "communication-preferences",
        {
          action: "update",
          token: normalizeToken(token),
          preferences,
        },
        {
          skipAuth: true,
          preferFreshSession: false,
          timeoutMs: 4500,
        },
      ),
      5000,
      "Saving communication preferences",
    );
  } catch (error) {
    throw friendlyCommunicationError(
      error,
      "AfriMed™ could not save these communication preferences.",
    );
  }
}

export async function unsubscribeCommunicationEmail(token) {
  try {
    return await withClientTimeout(
      callFunctionRaw(
        "communication-preferences",
        {
          action: "unsubscribe",
          token: normalizeToken(token),
        },
        {
          skipAuth: true,
          preferFreshSession: false,
          timeoutMs: 4500,
        },
      ),
      5000,
      "Unsubscribe request",
    );
  } catch (error) {
    throw friendlyCommunicationError(
      error,
      "AfriMed™ could not complete this unsubscribe request.",
    );
  }
}
